import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ACCOUNT_QUERY_KEYS } from '@/lib/constants/account';
import { authKeys } from '@/features/auth/queryKeys';
import { AccountApi } from '../AccountApi';
import { AccountUtils } from '../AccountUtils';
import type { IBillingAccountViewData, IUpgradeTrialReq } from '@/lib/types/Billing';

/** Mutation to update the payment method (billing account). */
export function useUpdatePaymentMethod() {
  const { authData, customerInfo } = useAuth();
  const queryClient = useQueryClient();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  return useMutation({
    mutationFn: ({
      billingAccountId,
      data,
    }: {
      billingAccountId: string;
      data: IBillingAccountViewData;
    }) => {
      if (!parentCId) throw new Error('Unable to resolve billing account');
      return AccountApi.updatePaymentMethod(parentCId, billingAccountId, data);
    },
    onSuccess: () => {
      if (parentCId) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.paymentMethod(parentCId) });
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.billingPlan(parentCId) });
      }
    },
  });
}

/** Mutation to upgrade from trial to paid plan. */
export function useUpgradeTrial() {
  const { authData, customerInfo } = useAuth();
  const queryClient = useQueryClient();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);
  const userCId = authData?.cpuser.core.cId ?? null;

  return useMutation({
    mutationFn: (req: IUpgradeTrialReq) => AccountApi.upgradeTrial(req),
    onSuccess: () => {
      if (parentCId) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.billingPlan(parentCId) });
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.paymentMethod(parentCId) });
      }
      if (userCId) {
        queryClient.invalidateQueries({ queryKey: authKeys.customerInfo(userCId) });
      }
    },
  });
}
